import { useState } from 'react';
import { api, errorMessage } from '../../shared/api/client';
import { Alert, Button } from '../../shared/ui/components';
import { useMe } from '../session/SessionProvider';

interface Library {
  data: string | null;
  label: string;
  songs: number;
  media: number;
  /** Videos e imágenes que EasyWorship tiene registrados pero ya no están en esta computadora. */
  missing?: number;
  missingSample?: string[];
}

interface EwSong {
  title: string;
  author: string | null;
  lyrics: string;
  notes: string | null;
}

interface EwApi {
  find: () => Promise<Library[]>;
  pick: () => Promise<Library | null>;
  songs: (data: string) => Promise<EwSong[]>;
  copyMedia: (data: string) => Promise<{ copied: number; skipped: number; renamed?: number; removed?: number; converted?: number; failed?: number; missing?: number; missingSample?: string[] }>;
  onProgress?: (fn: (p: { done: number; total: number; name: string | null }) => void) => () => void;
}

type WithApp = Window & { keoraApp?: { easyworship?: EwApi } };
const ewApi = (): EwApi | null => (window as WithApp).keoraApp?.easyworship ?? null;

/** Tandas para no mandar miles de cantos en una sola petición. */
const BATCH = 200;

/**
 * Importar de EasyWorship (Configuración de la app): la app busca la biblioteca de EasyWorship en
 * esta computadora, pasa los cantos a Cantos (sin duplicar títulos) y copia sus videos e imágenes a
 * la carpeta de la app. EasyWorship no se modifica.
 */
export function EasyWorshipCard() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const [ew] = useState(ewApi);
  const [libraries, setLibraries] = useState<Library[] | null>(null);
  const [chosen, setChosen] = useState<string>('');
  const [songs, setSongs] = useState(true);
  const [media, setMedia] = useState(true);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!churchId) return null;

  async function run<T>(fn: () => Promise<T>): Promise<T | undefined> {
    setBusy(true);
    setError(null);
    try {
      return await fn();
    } catch (e) {
      setError(errorMessage(e));
      return undefined;
    } finally {
      setBusy(false);
      setProgress(null);
    }
  }

  async function find() {
    if (!ew) return;
    setResult(null);
    const list = await run(() => ew.find());
    if (list) {
      setLibraries(list);
      setChosen(list[0]?.data ?? '');
    }
  }

  async function pick() {
    if (!ew) return;
    setResult(null);
    const lib = await run(() => ew.pick());
    if (!lib) return;
    if (!lib.data) return setError(`En «${lib.label}» no está la biblioteca de EasyWorship (falta Songs.db).`);
    setLibraries((prev) => [lib, ...(prev ?? []).filter((l) => l.data !== lib.data)]);
    setChosen(lib.data);
  }

  async function importAll() {
    if (!ew || !chosen) return;
    setResult(null);
    await run(async () => {
      const done: string[] = [];
      if (songs) {
        setProgress('Leyendo los cantos de EasyWorship…');
        const list = await ew.songs(chosen);
        let created = 0;
        let skipped = 0;
        const failed: string[] = [];
        for (let i = 0; i < list.length; i += BATCH) {
          setProgress(`Pasando cantos a KEORA… ${Math.min(i + BATCH, list.length)} de ${list.length}`);
          const batch = list.slice(i, i + BATCH);
          try {
            const r = await api.post<{ created: number; skipped: number; failed?: string[] }>(`/worship/churches/${churchId}/songs/import`, { songs: batch });
            created += r.created;
            skipped += r.skipped;
            failed.push(...(r.failed ?? []));
          } catch {
            // Una tanda con problemas no detiene las demás.
            failed.push(...batch.map((x) => x.title));
          }
        }
        done.push(`${created} ${created === 1 ? 'canto nuevo' : 'cantos nuevos'} en Cantos${skipped ? ` (${skipped} ${skipped === 1 ? 'ya estaba y no se tocó' : 'ya estaban y no se tocaron'})` : ''}`);
        if (failed.length) setError(`No se pudieron pasar ${failed.length} ${failed.length === 1 ? 'canto' : 'cantos'}: ${failed.slice(0, 10).join(', ')}${failed.length > 10 ? '…' : ''}. Vuelve a tocar «Importar» para reintentarlos.`);
      }
      if (media) {
        setProgress('Copiando videos e imágenes a esta computadora…');
        // Los videos WMV o AVI se convierten uno por uno y pueden tardar: se muestra en cuál va.
        const stop = ew.onProgress?.((p) => {
          if (p.name) setProgress(`Copiando videos e imágenes… ${p.done + 1} de ${p.total}: ${p.name}`);
        });
        let r;
        try {
          r = await ew.copyMedia(chosen);
        } finally {
          stop?.();
        }
        done.push(`${r.copied} ${r.copied === 1 ? 'archivo copiado' : 'archivos copiados'} a «En esta computadora»${r.skipped ? ` (${r.skipped} ${r.skipped === 1 ? 'ya estaba' : 'ya estaban'})` : ''}`);
        if (r.converted) done.push(`${r.converted} ${r.converted === 1 ? 'video o audio convertido' : 'videos o audios convertidos'} a un formato que se proyecta`);
        if (r.missing) done.push(`${r.missing} ${r.missing === 1 ? 'archivo registrado en EasyWorship no está' : 'archivos registrados en EasyWorship no están'} en esta computadora`);
        if (r.failed) setError(`${r.failed} ${r.failed === 1 ? 'video no se pudo convertir' : 'videos no se pudieron convertir'} (puede estar dañado). Los demás sí se importaron.`);
        if (r.renamed) done.push(`${r.renamed} ${r.renamed === 1 ? 'archivo tomó' : 'archivos tomaron'} su nombre de EasyWorship`);
        if (r.removed) done.push(`${r.removed} ${r.removed === 1 ? 'miniatura quitada' : 'miniaturas quitadas'}`);
      }
      setResult(`Listo: ${done.join('; ')}.`);
    });
  }

  const lib = libraries?.find((l) => l.data === chosen) ?? null;
  return (
    <div className="card" role="region" aria-label="Importar de EasyWorship">
      <h2>Importar de EasyWorship</h2>
      <p className="small muted">
        Si esta computadora tiene EasyWorship, KEORA busca su biblioteca y pasa los cantos a Cantos (con sus partes, autor y derechos) y copia los videos e imágenes a «En esta computadora» (los videos WMV o AVI se convierten a MP4 para que se puedan proyectar; la primera vez puede tardar). EasyWorship no se
        modifica y los cantos que ya existen no se duplican.
      </p>
      {!ew ? (
        <Alert tone="info">Para importar de EasyWorship instala la versión nueva de KEORA Multimedia (se descarga en Multimedia desde el navegador).</Alert>
      ) : (
        <>
          {error ? <Alert>{error}</Alert> : null}
          {result ? <Alert tone="success">{result}</Alert> : null}
          {progress ? (
            <p className="small" role="status">
              {progress}
            </p>
          ) : null}
          {libraries === null ? (
            <Button busy={busy} onClick={() => void find()}>
              Buscar EasyWorship en esta computadora
            </Button>
          ) : libraries.length === 0 ? (
            <>
              <p className="small">No encontré EasyWorship en esta computadora. Si lo tienes en otra carpeta, elígela.</p>
              <div className="actions">
                <Button variant="secondary" busy={busy} onClick={() => void pick()}>
                  Elegir la carpeta de EasyWorship
                </Button>
                <button type="button" className="link-button" onClick={() => void find()}>
                  Buscar de nuevo
                </button>
              </div>
            </>
          ) : (
            <div className="form">
              {libraries.length > 1 ? (
                <div className="field">
                  <label htmlFor="ew-library">Biblioteca</label>
                  <select id="ew-library" value={chosen} onChange={(e) => setChosen(e.target.value)}>
                    {libraries.map((l) => (
                      <option key={l.data} value={l.data ?? ''}>
                        {l.label} · {l.songs} cantos
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              {lib ? (
                <p>
                  Encontré <strong>{lib.songs}</strong> {lib.songs === 1 ? 'canto' : 'cantos'} y <strong>{lib.media}</strong> {lib.media === 1 ? 'video o imagen' : 'videos e imágenes'}.
                </p>
              ) : null}
              {lib?.missing ? (
                <Alert tone="info">
                  <p>
                    EasyWorship tiene registrados {lib.missing} {lib.missing === 1 ? 'video o imagen que ya no está' : 'videos o imágenes que ya no están'} en esta computadora (se movieron, se borraron o están en otro disco o memoria USB). Conecta ese disco o
                    copia esos archivos a esa ruta y vuelve a importar. Por ejemplo:
                  </p>
                  <ul className="small">
                    {(lib.missingSample ?? []).map((f) => (
                      <li key={f}>{f}</li>
                    ))}
                  </ul>
                </Alert>
              ) : null}
              <label className="check">
                <input type="checkbox" checked={songs} onChange={(e) => setSongs(e.target.checked)} /> Cantos (se suben a Cantos de la iglesia)
              </label>
              <label className="check">
                <input type="checkbox" checked={media} onChange={(e) => setMedia(e.target.checked)} /> Videos e imágenes (se copian a esta computadora, no se suben)
              </label>
              <div className="actions">
                <Button busy={busy} disabled={!songs && !media} onClick={() => void importAll()}>
                  Importar
                </Button>
                <button type="button" className="link-button" disabled={busy} onClick={() => void pick()}>
                  Elegir otra carpeta
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
